const HeroSkeletonLoader = () => {
  return (
    <div className="fixed inset-0 z-50 min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Premium Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        
        {/* Floating Orbs */}
        <div className="absolute rounded-full top-1/4 left-10 w-72 h-72 bg-blue-500/10 blur-3xl floating"></div>
        <div className="absolute rounded-full top-1/3 right-16 w-96 h-96 bg-purple-500/8 blur-3xl floating-delayed"></div>
        <div className="absolute rounded-full bottom-1/4 left-1/3 w-80 h-80 bg-cyan-500/8 blur-3xl floating-delayed-2"></div>
        
        {/* Geometric Patterns */}
        <div className="absolute inset-0 opacity-30">
          <svg className="absolute w-32 h-32 top-20 left-20 text-blue-400/20 animate-rotate" viewBox="0 0 100 100">
            <polygon points="50,5 95,25 95,75 50,95 5,75 5,25" fill="none" stroke="currentColor" strokeWidth="0.5" />
          </svg>
          <svg className="absolute w-24 h-24 bottom-32 right-32 text-purple-400/20 animate-pulse" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="0.5" />
          </svg>
          <svg className="absolute w-16 h-16 top-1/2 left-1/4 text-cyan-400/20 animate-bounce" viewBox="0 0 100 100">
            <rect x="25" y="25" width="50" height="50" fill="none" stroke="currentColor" strokeWidth="0.5" />
          </svg>
        </div>
      </div>

      {/* Navbar Skeleton */}
      <div className="fixed top-0 left-0 right-0 z-50 glass-skeleton backdrop-blur-sm">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-600/30 shimmer-animation"></div>
              <div className="w-48 h-6 rounded-full bg-gradient-to-r from-blue-400/20 to-purple-400/20 shimmer-animation"></div>
            </div>
            <div className="hidden md:block">
              <div className="flex space-x-6">
                <div className="w-16 h-8 rounded-full bg-white/10 shimmer-animation"></div>
                <div className="w-16 h-8 rounded-full bg-white/10 shimmer-animation"></div>
                <div className="w-16 h-8 rounded-full bg-white/10 shimmer-animation"></div>
                <div className="w-20 h-10 rounded-full bg-gradient-to-r from-blue-600/30 to-purple-600/30 shimmer-animation"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section Skeleton */}
      <section className="relative flex items-center justify-center min-h-screen pt-16 pb-10 overflow-hidden lg:pt-8">
        <div className="relative z-10 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20">
            
            {/* Left Side - Text Content Skeleton */}
            <div className="text-center lg:text-left">
              
              {/* Premium Badge Skeleton */}
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full glass-skeleton lg:hidden">
                <div className="w-4 h-4 rounded-full bg-yellow-400/30 shimmer-animation"></div>
                <div className="h-4 rounded bg-gray-300/20 shimmer-animation w-60"></div>
                <div className="w-2 h-2 rounded-full bg-green-400/30 animate-pulse"></div>
              </div>

              {/* Main Headline Skeleton - exact text-headline sizing */}
              <div className="mb-6">
                <div className="w-full h-12 rounded-lg bg-gradient-to-r from-blue-400/30 via-purple-400/30 to-blue-400/30 shimmer-animation sm:h-14 md:h-16 lg:h-20"></div>
              </div>

              {/* Subtitle Skeleton - exact text-subtitle and text-body sizing */}
              <div className="mb-10">
                <div className="mb-6 space-y-2">
                  <div className="w-full h-6 rounded bg-gray-300/20 shimmer-animation sm:h-7 md:h-8"></div>
                  <div className="w-5/6 h-6 rounded bg-gray-300/20 shimmer-animation sm:h-7 md:h-8"></div>
                </div>
                <div className="space-y-2">
                  <div className="w-full h-4 rounded bg-gray-400/20 shimmer-animation"></div>
                  <div className="w-11/12 h-4 rounded bg-gray-400/20 shimmer-animation"></div>
                  <div className="w-4/5 h-4 rounded bg-gray-400/20 shimmer-animation"></div>
                </div>
              </div>

              {/* Key Features Cards Skeleton - Mobile Only - exact card-premium styling */}
              <div className="grid grid-cols-1 gap-4 mb-12 sm:grid-cols-2 lg:hidden">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="p-6 transition-transform duration-300 cursor-pointer card-skeleton group hover:scale-105">
                    <div className="flex items-center gap-4">
                      <div className={`flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-xl shimmer-animation ${
                        index === 0 ? 'bg-gradient-to-br from-blue-500/30 to-blue-600/30 shadow-glow-primary' :
                        index === 1 ? 'bg-gradient-to-br from-purple-500/30 to-purple-600/30 shadow-glow-accent' :
                        index === 2 ? 'bg-gradient-to-br from-cyan-500/30 to-cyan-600/30' :
                        'bg-gradient-to-br from-green-500/30 to-green-600/30'
                      }`}>
                        <div className="w-6 h-6 rounded bg-white/30 shimmer-animation"></div>
                      </div>
                      <div className="flex-1">
                        <div className={`mb-2 bg-white/20 rounded shimmer-animation ${
                          index === 0 ? 'h-4 w-28 text-sm lg:text-base font-bold' : 
                          index === 1 ? 'h-4 w-24 text-sm lg:text-base font-bold' : 
                          index === 2 ? 'h-4 w-36 text-sm lg:text-base font-bold' : 
                          'h-4 w-26 text-sm lg:text-base font-bold'
                        }`}></div>
                        <div className={`bg-gray-400/20 rounded shimmer-animation ${
                          index === 0 ? 'h-3 w-36 text-xs lg:text-sm' : 
                          index === 1 ? 'h-3 w-32 text-xs lg:text-sm' : 
                          index === 2 ? 'h-3 w-28 text-xs lg:text-sm' : 
                          'h-3 w-30 text-xs lg:text-sm'
                        }`}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons Skeleton - exact btn-primary and btn-secondary styling */}
              <div className="flex flex-col justify-center gap-6 sm:flex-row lg:justify-start">
                <div className="px-8 py-4 text-lg shadow-2xl group btn-primary bg-gradient-to-r from-blue-600/30 via-blue-700/30 to-purple-600/30 hover:from-blue-500/30 hover:via-blue-600/30 hover:to-purple-500/30 shadow-blue-500/25 hover:shadow-blue-400/40 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-48 h-6 rounded bg-white/20 shimmer-animation"></div>
                    <div className="w-5 h-5 rounded bg-white/30 shimmer-animation"></div>
                  </div>
                </div>
                
                <div className="px-8 py-4 text-lg border-2 group btn-secondary border-gray-600/50 hover:border-blue-400/50 rounded-xl glass-skeleton">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded bg-white/20 shimmer-animation"></div>
                    <div className="w-48 h-6 rounded bg-white/20 shimmer-animation"></div>
                  </div>
                </div>
              </div>

              {/* Trust Indicators Skeleton */}
              <div className="pt-8 mt-12 border-t border-gray-700/50">
                <div className="flex flex-col items-center gap-4 lg:items-start">
                  <div className="w-64 h-4 text-sm rounded bg-gray-400/20 shimmer-animation"></div>
                  <div className="flex items-center gap-8 opacity-60">
                    <div className="w-24 h-4 text-sm font-medium rounded bg-gray-300/20 shimmer-animation"></div>
                    <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
                    <div className="w-20 h-4 text-sm font-medium rounded bg-gray-300/20 shimmer-animation"></div>
                    <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
                    <div className="w-32 h-4 text-sm font-medium rounded bg-gray-300/20 shimmer-animation"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Premium Visual Skeleton - exact glass shadow-premium styling */}
            <div className="flex items-center justify-center">
              <div className="relative">
                
                {/* Main Central Element Skeleton */}
                <div className="relative z-10">
                  <div className="flex items-center justify-center mx-auto rounded-full w-80 h-80 glass shadow-premium lg:w-96 lg:h-96 shimmer-animation">
                    <div className="flex items-center justify-center w-64 h-64 border rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 backdrop-blur-xl border-white/10 lg:w-80 lg:h-80">
                      <div className="flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-blue-600/30 to-purple-600/30 shadow-glow-primary lg:w-40 lg:h-40">
                        <div className="object-contain w-20 h-20 rounded-lg lg:w-24 lg:h-24 bg-white/30 shimmer-animation animate-pulse" style={{
                          filter: 'brightness(1.5) contrast(1.2) saturate(1.5) drop-shadow(0 0 20px rgba(59, 130, 246, 0.5))'
                        }}></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Orbiting Elements Skeleton - exact glass rounded-2xl */}
                <div className="absolute inset-0 animate-rotate" style={{ animationDuration: '20s' }}>
                  <div className="absolute flex items-center justify-center w-16 h-16 transform -translate-x-1/2 -top-8 left-1/2 glass rounded-2xl">
                    <div className="w-8 h-8 rounded bg-blue-400/30 shimmer-animation"></div>
                  </div>
                  <div className="absolute flex items-center justify-center w-16 h-16 transform -translate-y-1/2 top-1/2 -right-8 glass rounded-2xl">
                    <div className="w-8 h-8 rounded-full bg-purple-400/30 shimmer-animation"></div>
                  </div>
                  <div className="absolute flex items-center justify-center w-16 h-16 transform -translate-x-1/2 -bottom-8 left-1/2 glass rounded-2xl">
                    <div className="w-8 h-8 bg-cyan-400/30 shimmer-animation"></div>
                  </div>
                  <div className="absolute flex items-center justify-center w-16 h-16 transform -translate-y-1/2 top-1/2 -left-8 glass rounded-2xl">
                    <div className="w-8 h-8 rounded-full bg-yellow-400/30 shimmer-animation"></div>
                  </div>
                </div>
                
                {/* Accent Circles Skeleton */}
                <div className="absolute w-24 h-24 rounded-full top-4 right-4 bg-gradient-to-br from-blue-500/10 to-transparent blur-xl animate-pulse"></div>
                <div className="absolute w-32 h-32 rounded-full bottom-8 left-8 bg-gradient-to-br from-purple-500/10 to-transparent blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                <div className="absolute w-20 h-20 rounded-full top-1/3 left-4 bg-gradient-to-br from-cyan-500/10 to-transparent blur-xl animate-pulse" style={{ animationDelay: '2s' }}></div>
                
                {/* Background Glow */}
                <div className="absolute inset-0 scale-150 rounded-full bg-gradient-to-br from-blue-500/5 via-purple-600/5 to-cyan-500/5 blur-3xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scroll Indicator Skeleton */}
      <div className="absolute hidden transform -translate-x-1/2 md:block bottom-8 left-1/2">
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <div className="h-3 text-xs font-medium rounded w-28 bg-gray-400/20 shimmer-animation"></div>
          <div className="flex justify-center w-6 h-10 border-2 border-gray-600 rounded-full">
            <div className="w-1 h-3 mt-2 rounded-full bg-blue-400/30 animate-pulse"></div>
          </div>
        </div>
      </div>

      <style>{`
        .shimmer-animation {
          background: linear-gradient(
            90deg,
            rgba(59, 130, 246, 0.1) 0%,
            rgba(147, 51, 234, 0.2) 25%,
            rgba(59, 130, 246, 0.3) 50%,
            rgba(147, 51, 234, 0.2) 75%,
            rgba(59, 130, 246, 0.1) 100%
          );
          background-size: 200% 100%;
          animation: shimmer 2.5s infinite;
        }

        .glass-skeleton {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .card-skeleton {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 1rem;
          transition: all 0.3s ease;
        }

        .card-skeleton:hover {
          transform: scale(1.05);
          background: rgba(255, 255, 255, 0.06);
        }

        .floating-delayed-2 {
          animation: floating 3s ease-in-out infinite;
          animation-delay: 2s;
        }

        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }

        @keyframes floating {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(5deg); }
        }

        .floating {
          animation: floating 3s ease-in-out infinite;
        }

        .floating-delayed {
          animation: floating 3s ease-in-out infinite;
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
};

export default HeroSkeletonLoader;
